import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUp, ChevronDown, ChevronUp, Hand, LayoutList, LocateFixed, MapPin, Navigation, RefreshCw, SlidersHorizontal, Undo2, Check, Route as RouteIcon } from 'lucide-react';
import { distanceKm, formatDateTime, formatDay } from '@nu/shared';
import { api } from '../../api/client.js';
import { buzz, currentPosition } from '../../native/device.js';
import SurveyForm from './SurveyForm.jsx';
import RouteMap from '../../components/RouteMap.jsx';
import { STATUS_CLASS, dismiss, mapsUrl, pickUpJob, releaseJob, useSurveys } from './store.js';

// App: Surveys. The supervisor's Network Plus jobs to survey on site.
// Assigned to me = given to me by the office, still to survey; Unassigned =
// nobody's yet (Take it); Surveyed by me = ones I've done. Each is a toggle:
// tap the one that's on to turn it off and see every job. People with the
// app only (no office access) get just what the office gives them: no
// Unassigned, no Take it or Unassign (the server sends only theirs). Nearest first,
// from where the phone is (straight line); works offline from the last list.
const VIEWS = [
  ['mine', 'Assigned to me'],
  ['open', 'Unassigned'],
  ['done', 'Surveyed by me'],
];

// How the list is ordered; remembered on the phone. Jobs with no distance
// (postcode not found, or no location) go last on the distance sorts.
const byPostcode = (a, b) => a.postcode.localeCompare(b.postcode) || a.address.localeCompare(b.address);
const SORTS = {
  near: { label: 'Nearest first', fn: (a, b) => (a.km ?? 1e9) - (b.km ?? 1e9) || byPostcode(a, b) },
  far: { label: 'Furthest first', fn: (a, b) => (b.km ?? -1) - (a.km ?? -1) || byPostcode(a, b) },
  postcode: { label: 'Postcode A–Z', fn: byPostcode },
};
const SORT_KEY = 'nu.surveys.sort';
const onIt = (j, me) => (j.surveyorIds || []).includes(me.id);
const appOnly = (me) => !me.capabilities.includes('office.use');
const FOLD_KEY = 'nu.surveys.folded';

export default function FieldSurveys({ me, shell = 'field' }) {
  const { data, jobs, status, refused, reload } = useSurveys();
  const [chosen, setView] = useState(null); // null = not picked yet, '' = toggled off (all jobs)
  const canEdit = me.capabilities.includes('page.surveys.edit');
  const canTake = canEdit && !appOnly(me);
  const views = appOnly(me) ? VIEWS.filter(([id]) => id !== 'open') : VIEWS;
  const [area, setArea] = useState('');
  const [here, setHere] = useState(null);
  const [locating, setLocating] = useState(false);
  const bar = useRef(null);
  // Picking jobs for a route: hold a card to start, then tap to add or remove.
  const [picked, setPicked] = useState(() => new Set());
  const picking = picked.size > 0;
  const held = useRef(false); // the tap that ends a long press isn't a click
  const toggle = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const timer = useRef(null);
  const startAt = useRef(null);
  const cancelHold = () => { clearTimeout(timer.current); timer.current = null; };
  const hold = (id) => ({
    onPointerDown: (e) => {
      if (e.target.closest('button')) return; // Take it / Unassign
      startAt.current = { x: e.clientX, y: e.clientY };
      held.current = false;
      cancelHold();
      timer.current = setTimeout(() => { held.current = true; toggle(id); buzz(); }, 450);
    },
    onPointerMove: (e) => {
      const s = startAt.current;
      if (timer.current && s && Math.hypot(e.clientX - s.x, e.clientY - s.y) > 10) cancelHold();
    },
    onPointerUp: cancelHold,
    onPointerCancel: cancelHold,
    onContextMenu: (e) => e.preventDefault(),
  });
  const [routing, setRouting] = useState(false);
  const [routeResult, setRouteResult] = useState(null);
  // The route opens at the top of the screen.
  useEffect(() => { if (routeResult) document.querySelector('.field-main, .office-main')?.scrollTo(0, 0); }, [routeResult]);
  const route = async () => {
    if (!here) { setRouteResult({ error: 'We don’t know where you are yet. Tap Refresh location (or turn on location) and try again.' }); return; }
    setRouting(true);
    try {
      setRouteResult(await api('/surveys/route', { method: 'POST', body: { from: { lat: here.lat, lon: here.lon }, jobIds: [...picked] } }));
    } catch (e) {
      setRouteResult({ error: e?.code === 'offline' ? 'No signal: the route needs a connection.' : e?.message || 'The route couldn’t be worked out. Try again.' });
    } finally {
      setRouting(false);
    }
  };
  // The arrow next to the heading opens and closes the whole card: open, every
  // option and both refresh buttons; closed, one slim row (what's shown, and
  // the refresh buttons as icons), for more of the list on screen. Their
  // choice is kept on the phone.
  const [folded, setFolded] = useState(() => {
    try { return localStorage.getItem(FOLD_KEY) === '1'; } catch { return false; }
  });
  const fold = (v) => {
    setFolded(v);
    try { localStorage.setItem(FOLD_KEY, v ? '1' : '0'); } catch { /* this visit only */ }
  };
  const compact = folded;
  const [sort, setSort] = useState(() => {
    try { return SORTS[localStorage.getItem(SORT_KEY)] ? localStorage.getItem(SORT_KEY) : 'near'; } catch { return 'near'; }
  });
  const pickSort = (s) => {
    setSort(s);
    try { localStorage.setItem(SORT_KEY, s); } catch { /* this visit only */ }
  };

  // Distances are from the surveyor's van (Samsara); the phone's own
  // location if their van can't be found, or when they tap Nearest to me.
  const fromVan = async () => {
    setLocating(true);
    const p = await api('/surveys/position').then((d) => d.position, () => null);
    setHere(p ? { ...p, from: 'van' } : await phone());
    setLocating(false);
  };
  const phone = async () => {
    const p = await currentPosition({ timeout: 8000 });
    return p ? { lat: p.lat, lon: p.lng, from: 'phone' } : null;
  };
  const fromPhone = async () => {
    setLocating(true);
    setHere(await phone());
    setLocating(false);
  };

  useEffect(() => { fromVan(); }, []);

  const assigned = jobs.filter((j) => onIt(j, me) && j.jobStatus === 'To Presite').length;
  const view = chosen ?? (assigned || appOnly(me) ? 'mine' : 'open');

  const list = useMemo(() => {
    const todo = (j) => j.jobStatus === 'To Presite';
    const pick = {
      mine: (j) => onIt(j, me) && todo(j),
      open: (j) => !j.surveyorIds?.length && todo(j),
      done: (j) => j.surveyedById === me.id,
    }[view] || (() => true);
    return jobs
      .filter((j) => pick(j) && (!area || j.areaCode === area))
      .map((j) => ({ ...j, km: here ? distanceKm(here, j) : null }))
      .sort(SORTS[sort].fn);
  }, [jobs, view, area, here, me, sort]);

  const counts = useMemo(() => ({
    mine: jobs.filter((j) => onIt(j, me) && j.jobStatus === 'To Presite').length,
    open: jobs.filter((j) => !j.surveyorIds?.length && j.jobStatus === 'To Presite').length,
    done: jobs.filter((j) => j.surveyedById === me.id).length,
  }), [jobs, me]);

  const viewLabel = views.find(([id]) => id === view)?.[1] || 'All jobs';
  const areaLabel = data?.areas?.find((a) => a.code === area)?.name || 'All areas';

  if (routeResult) {
    return (
      <div className="stack">
        <RouteView result={routeResult} jobs={jobs} here={here} onClose={() => setRouteResult(null)} />
      </div>
    );
  }

  return (
    <div className="stack">
      {/* One card that stays at the top while the list scrolls: the title,
          the options (the arrow folds them away), where the distances are
          from, and the two refresh buttons. */}
      <section className={`card survey-bar${compact ? ' compact' : ''}`} ref={bar}>
        <div className="survey-area-head">
          <h1 className="stock-title">Surveys</h1>
          <button type="button" className="btn icon-only survey-fold" aria-expanded={!compact} aria-label={compact ? 'Show more' : 'Show less'} title={compact ? 'Show more' : 'Show less'} onClick={() => fold(!compact)}>
            {compact ? <ChevronDown size={20} aria-hidden="true" /> : <ChevronUp size={20} aria-hidden="true" />}
          </button>
          {shell === 'office' && <Link to="/surveys?board=1" className="btn with-icon"><LayoutList size={16} aria-hidden="true" /> Office board</Link>}
        </div>
        {refused.map((m) => (
          <p key={m.id} className="notice-line notice-bad" role="alert">
            {m.message || 'A change wasn’t accepted.'} <button type="button" className="link-btn" onClick={() => dismiss(m)}>OK</button>
          </p>
        ))}
        {compact && (
          <div className="survey-bar-row">
            <button type="button" className="survey-bar-toggle" onClick={() => fold(false)} aria-label="Show the options">
              <SlidersHorizontal size={18} aria-hidden="true" />
              <span><strong>{viewLabel}</strong> ({view ? counts[view] : list.length}) · {areaLabel} · {SORTS[sort].label}</span>
            </button>
            <button type="button" className="btn icon-only" aria-label="Refresh jobs" title="Refresh jobs" onClick={reload} disabled={status === 'loading'}>
              <RefreshCw size={18} aria-hidden="true" className={status === 'loading' ? 'spin' : ''} />
            </button>
            <button type="button" className="btn icon-only" aria-label="Refresh location" title="Refresh location" onClick={fromVan} disabled={locating}>
              <MapPin size={18} aria-hidden="true" className={locating ? 'spin' : ''} />
            </button>
          </div>
        )}
        {!compact && (
          <>
            <div className="survey-views">
              {views.map(([id, label]) => (
                <button key={id} type="button" className={`chip${view === id ? ' on' : ''}`} aria-pressed={view === id} onClick={() => setView(view === id ? '' : id)}>
                  {label} ({counts[id]})
                </button>
              ))}
            </div>
            <div className="survey-views">
              {data?.areas?.length > 1 && (
                <select value={area} onChange={(e) => setArea(e.target.value)} aria-label="Area">
                  <option value="">All areas</option>
                  {data.areas.map((a) => <option key={a.code} value={a.code}>{a.name}</option>)}
                </select>
              )}
              <select value={sort} onChange={(e) => pickSort(e.target.value)} aria-label="Sort">
                {Object.entries(SORTS).map(([id, s]) => <option key={id} value={id}>Sort: {s.label}</option>)}
              </select>
              <button type="button" className="btn with-icon" onClick={fromPhone} disabled={locating}><LocateFixed size={18} aria-hidden="true" /> Nearest to me</button>
            </div>
          </>
        )}
        <div className="survey-refresh">
          <button type="button" className="btn with-icon" onClick={reload} disabled={status === 'loading'}>
            <RefreshCw size={16} aria-hidden="true" className={status === 'loading' ? 'spin' : ''} /> <span>Refresh<br />jobs</span>
          </button>
          <button type="button" className="btn with-icon" onClick={fromVan} disabled={locating}>
            <MapPin size={16} aria-hidden="true" className={locating ? 'spin' : ''} /> <span>Refresh<br />location</span>
          </button>
        </div>
        <p className="small survey-from">
          <MapPin size={14} aria-hidden="true" />{' '}
          {locating ? 'Finding where you are…'
            : here?.from === 'van' ? <>From {here.van ? <strong>{here.van}</strong> : 'your van'}{here.place ? `, ${here.place}` : ''}{here.at ? ` (${formatDateTime(here.at).slice(11)})` : ''}</>
              : here ? 'From your phone’s location.'
                : 'No van or location found. Turn on location and tap Nearest to me.'}
        </p>
        {!data && <p className="muted">{status === 'error' ? 'The surveys can’t be loaded with no signal yet.' : 'Loading…'}</p>}
        {data && !list.length && (
          <p className="muted">{view === 'mine' ? (appOnly(me) ? 'Nothing assigned to you yet. The office gives out surveys.' : 'Nothing assigned to you. Take some from Unassigned.') : view === 'open' ? 'Nothing unassigned.' : view === 'done' ? 'You haven’t surveyed any yet.' : 'No survey jobs.'}</p>
        )}
      </section>
      {list.map((j) => (
        <div key={j.id} className={`card survey-job${picked.has(j.id) ? ' picked' : ''}`} {...hold(j.id)}>
          {picking && <span className={`pick-box${picked.has(j.id) ? ' on' : ''}`} aria-hidden="true">{picked.has(j.id) && <Check size={16} />}</span>}
          <Link
            to={`/surveys/${j.id}`}
            className="survey-job-main"
            aria-pressed={picking ? picked.has(j.id) : undefined}
            onClick={(e) => { if (picking || held.current) { e.preventDefault(); if (!held.current) toggle(j.id); } held.current = false; }}
          >
            <strong>{j.address}</strong>
            <span>{j.postcode}</span>
            <span className="small muted">{j.jobNumber}{j.surveyorName && view !== 'mine' ? ` · ${j.surveyorName}` : ''}</span>
            {j.km != null && <span className="survey-km"><MapPin size={14} aria-hidden="true" /> {j.km < 10 ? j.km.toFixed(1) : Math.round(j.km)} km {here?.from === 'van' ? 'from your van' : 'from you'}</span>}
            <span className={`status ${STATUS_CLASS[j.jobStatus] || ''}`}>{j.sending ? 'Sending…' : j.jobStatus}</span>
          </Link>
          {canTake && !j.surveyorIds?.length && j.jobStatus === 'To Presite' && (
            <button type="button" className="btn primary survey-take with-icon" onClick={() => pickUpJob(me, j)}>
              <Hand size={18} aria-hidden="true" /> Take it
            </button>
          )}
          {canTake && onIt(j, me) && j.jobStatus === 'To Presite' && (
            <button type="button" className="btn survey-take with-icon" onClick={() => releaseJob(me, j)}>
              <Undo2 size={18} aria-hidden="true" /> Unassign
            </button>
          )}
        </div>
      ))}
      {picking && (
        <div className="pick-bar" role="region" aria-label="Selected jobs">
          <span><strong>{picked.size}</strong> selected</span>
          <button type="button" className="btn" onClick={() => setPicked(new Set())}>Clear</button>
          <button type="button" className="btn primary with-icon" disabled={routing} onClick={route}>
            <RouteIcon size={18} aria-hidden="true" className={routing ? 'spin' : ''} /> {routing ? 'Working out…' : 'Route'}
          </button>
        </div>
      )}
      {!picking && <ScrollTop />}
    </div>
  );
}

const fmtTime = (s) => {
  const m = Math.round(s / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} min`;
};
const fmtKm = (m) => `${(m / 1000).toFixed(m < 10_000 ? 1 : 0)} km`;

// The route for the selected jobs, as its own screen in the list's place (the
// app's top and bottom bars stay, and it scrolls like any page): the map,
// totals, each stop in order with the drive to it, and Google Maps (up to 10
// stops). Back returns to the list with the jobs still selected.
function RouteView({ result, jobs, here, onClose }) {
  const head = (title) => (
    <div className="route-head">
      <button type="button" className="link-btn back-link" onClick={onClose}><ArrowLeft size={18} aria-hidden="true" /> Back to the list</button>
      <h1 className="survey-title">{title}</h1>
    </div>
  );
  if (result.error) {
    return (
      <section className="card">
        {head('Route')}
        <p className="notice-line notice-bad">{result.error}</p>
      </section>
    );
  }
  const byId = new Map(jobs.map((j) => [j.id, j]));
  const stops = result.stops.map((s) => ({ ...s, job: byId.get(s.jobId) })).filter((s) => s.job);
  const pt = (j) => `${j.lat},${j.lon}`;
  const shown = stops.slice(0, 10);
  const last = shown[shown.length - 1]?.job;
  const via = shown.slice(0, -1).map((s) => pt(s.job)).join('|');
  const maps = last ? `https://www.google.com/maps/dir/?api=1&origin=${here.lat},${here.lon}&destination=${pt(last)}${via ? `&waypoints=${encodeURIComponent(via)}` : ''}&travelmode=driving` : null;
  return (
    <>
      <section className="card route-view">
        {head(`Route: ${stops.length} job${stops.length === 1 ? '' : 's'}`)}
        <RouteMap
          height={230}
          simple
          fullscreen
          className="route-sheet-map"
          routes={result.line?.length > 1 ? [{ key: 'route', colour: '#D9E021', casing: '#0B2340', line: result.line }] : []}
          stops={stops.map((s, i) => ({ key: String(s.jobId), coord: [s.job.lon, s.job.lat], label: String(i + 1), title: `${s.job.address}, ${s.job.postcode}` }))}
          homes={[{ key: 'start', coord: [here.lon, here.lat], title: here.from === 'van' ? (here.van || 'Your van') : 'You' }]}
        />
        <div className="route-totals">
          <div><span className="small muted">Driving time</span><strong>{fmtTime(result.totalSeconds)}</strong></div>
          <div><span className="small muted">Distance</span><strong>{fmtKm(result.totalMetres)}</strong></div>
        </div>
        <p className="small muted">From {here.from === 'van' ? (here.van || 'your van') : 'your phone’s location'}, in the quickest order. Driving only: time on each job isn’t included.</p>
        {result.missing?.length > 0 && <p className="small warn-text">{result.missing.length} job{result.missing.length === 1 ? ' isn’t' : 's aren’t'} on the map (postcode not found), so left out.</p>}
        <ol className="route-stops">
          {stops.map((s, i) => (
            <li key={s.jobId}>
              <span className="route-n">{i + 1}</span>
              <span className="route-what"><strong>{s.job.address}</strong><span className="small">{s.job.postcode}</span></span>
              <span className="small route-leg">{fmtTime(s.seconds)}<br />{fmtKm(s.metres)}</span>
            </li>
          ))}
        </ol>
        {maps && (
          <a className="btn primary with-icon route-maps" href={maps} target="_blank" rel="noreferrer">
            <Navigation size={18} aria-hidden="true" /> Open in Google Maps{stops.length > 10 ? ' (first 10 stops)' : ''}
          </a>
        )}
      </section>
    </>
  );
}

// "Back to top", once the list has been scrolled a screen or so down. The
// list scrolls inside the shell's main area, not the window.
function ScrollTop() {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const box = ref.current?.closest('.field-main, .office-main') || window;
    const top = () => (box === window ? window.scrollY : box.scrollTop);
    const on = () => setShown(top() > 600);
    box.addEventListener('scroll', on, { passive: true });
    return () => box.removeEventListener('scroll', on);
  }, []);
  const go = () => {
    const box = ref.current?.closest('.field-main, .office-main');
    (box || window).scrollTo({ top: 0, behavior: 'smooth' });
  };
  return (
    <span ref={ref}>
      {shown && (
        <button type="button" className="scroll-top" aria-label="Back to top" onClick={go}>
          <ArrowUp size={22} aria-hidden="true" />
        </button>
      )}
    </span>
  );
}

// App: /surveys/:id, one job on site.
export function FieldSurvey({ me }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, jobs, status } = useSurveys();
  const job = jobs.find((j) => String(j.id) === id);
  const canEdit = me.capabilities.includes('page.surveys.edit');
  const back = <button type="button" className="link-btn back-link" onClick={() => navigate('/surveys')}><ArrowLeft size={18} aria-hidden="true" /> Surveys</button>;

  if (!job) return <section className="card">{back}<p className="muted">{!data ? (status === 'error' ? 'Can’t load this with no signal yet.' : 'Loading…') : 'This survey job isn’t on the list any more.'}</p></section>;
  const canTake = canEdit && !appOnly(me);
  const theirs = job.surveyorIds?.length > 0 && !onIt(job, me);

  return (
    <div className="stack">
      <section className="card">
        {back}
        <h1 className="survey-title">{job.address}</h1>
        <p><strong>{job.postcode}</strong> · {job.areaName}</p>
        <dl className="survey-facts small">
          <div><dt>Job Number</dt><dd>{job.jobNumber}</dd></div>
          {job.floc && <div><dt>Floc</dt><dd>{job.floc}</dd></div>}
          <div><dt>Job Status</dt><dd><span className={`status ${STATUS_CLASS[job.jobStatus] || ''}`}>{job.jobStatus}</span></dd></div>
          <div><dt>Gang</dt><dd>{job.gangName ? `${job.gangName} (${job.teamName})` : 'None yet'}</dd></div>
          <div><dt>Surveyor</dt><dd>{job.surveyorName || 'Unassigned'}</dd></div>
          {job.npPlannedDate && <div><dt>Network Plus planned</dt><dd>{job.npPlannedDate}</dd></div>}
          {job.npComments && <div><dt>Network Plus comments</dt><dd>{job.npComments}</dd></div>}
          {job.plannedDate && <div><dt>Planned</dt><dd>{formatDay(job.plannedDate)}</dd></div>}
        </dl>
        <div className="actions">
          <a className="btn with-icon" href={mapsUrl(job)} target="_blank" rel="noreferrer"><Navigation size={18} aria-hidden="true" /> Directions</a>
          {!job.surveyorIds?.length && canTake && (
            <button type="button" className="btn with-icon" onClick={() => pickUpJob(me, job)}><Hand size={18} aria-hidden="true" /> Take it</button>
          )}
          {onIt(job, me) && job.jobStatus === 'To Presite' && canTake && (
            <button type="button" className="btn with-icon" onClick={() => releaseJob(me, job)}><Undo2 size={18} aria-hidden="true" /> Unassign</button>
          )}
        </div>
        {theirs && <p className="small warn-text">This job is {job.surveyorName}’s to survey. You can still fill it in if you’re there.</p>}
      </section>
      <section className="card">
        <h2>Survey</h2>
        <SurveyForm me={me} job={job} canEdit={canEdit} gps gangs={data?.gangs || []} photosOn={data?.photos !== false} onSent={() => navigate('/surveys')} />
      </section>
    </div>
  );
}
